import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Eye, Loader2, Plus } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Tooltip } from '../../../components/ui/tooltip';
import { MultiSelect, type Option } from '../../../components/ui/multi-select';
import { cn } from '../../../lib/utils';
import { convertValueFromInches } from '../../../utils/unitUtils';
import PipeFfitter from '../../project/components/dashboard/materials/PipeFfitter';
import { CollapsibleFilterBar } from '../../project/components/dashboard/materials/tabs/Filters/CollapsibleFilterBar';
import type { Project, DashboardProject } from '../../project/types/project';
import { extractPipeSizesFromPipeDetails } from '../../project/components/dashboard/materials/extractPipeSizesFromPipeDetails';
import { fetchAllPipeDetails } from '../api/fetchAllPipeDetails';
import {
  fetchPipeStatusInventory,
  type PipeStatusInventoryRow,
} from '../api/pipeStatusInventory';
import { inventoryHasMaterialForPipeId } from '../utils/matchInventoryMaterialByPipeId';
import { isProjectPipeFitterEnabled } from '../utils/isProjectPipeFitterEnabled';
import {
  buildFitterSetupTree,
  type FitterSetupPipeSize,
} from '../utils/buildFitterSetupTree';
import {
  buildFitterNavTree,
  collectFitterLeafGroupIds,
  fitterGroupNodeId,
  parseFitterGroupNodeId,
} from '../utils/buildFitterNavTree';
import {
  collectCollapsibleGroupIds,
  resolvePipeGroupSelection,
  type PipeGroupField,
} from '../utils/groupPipeMaterials';
import { PipeGroupNavPanel } from './PipeGroupNavPanel';

export type FitterAddSeed = {
  projectId: string;
  projectNumber?: string;
  projectName?: string;
  pipeNo: string;
  pipePipeId?: string;
};

type PipeInventoryFitterPanelProps = {
  projectList: Project[];
  materials: Array<Record<string, unknown>>;
  onAddFromFitter?: (seed: FitterAddSeed) => void;
};

type SetupCache = {
  loading?: boolean;
  error?: string | null;
  pipes?: FitterSetupPipeSize[];
};

type RowsCache = {
  loading?: boolean;
  error?: string | null;
  rows?: PipeStatusInventoryRow[];
  totalCount?: number;
  page?: number;
  search?: string;
};

/** Pipe Id search hits keyed by fitter group nav id. null = no active search. */
type SearchHits = Record<
  string,
  { projectId: string; pipeId: string; groupId: string; rows: PipeStatusInventoryRow[] }
>;

function isRealGroupId(value: string): boolean {
  const raw = String(value ?? '').trim();
  return Boolean(raw) && raw !== '—';
}

function formatProjectLabel(p: Project): string {
  return p.projectNumber ? `${p.projectName} (${p.projectNumber})` : p.projectName;
}

function hasPipeData(row: PipeStatusInventoryRow): boolean {
  if (Array.isArray(row.pipeData)) return row.pipeData.length > 0;
  if (row.pipeData && typeof row.pipeData === 'object') {
    return Object.keys(row.pipeData as object).length > 0;
  }
  return false;
}

function setupKey(projectId: string): string {
  return `setup:${projectId}`;
}

function rowsKey(projectId: string, pipeId: string, groupId: string): string {
  return `rows:${projectId}|${pipeId}|${groupId}`;
}

function statusBadgeClass(status: string | null | undefined): string {
  const s = String(status ?? '').trim();
  if (s === 'Available') return 'bg-green-100 text-green-800';
  if (s === 'Consumed') return 'bg-gray-100 text-gray-800';
  return 'bg-page-bg text-heritage-grey';
}

function unitHeaderSuffix(unit: 'in' | 'mm'): string {
  return unit === 'mm' ? 'MM' : 'IN';
}

function formatStoredInches(value: unknown, unit: 'in' | 'mm'): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return convertValueFromInches(n, unit).toFixed(2);
}

const ROW_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 350;

export const PipeInventoryFitterPanel: React.FC<PipeInventoryFitterPanelProps> = ({
  projectList,
  materials,
  onAddFromFitter,
}) => {
  const [setupByProject, setSetupByProject] = useState<Record<string, SetupCache>>({});
  const [rowsByLeaf, setRowsByLeaf] = useState<Record<string, RowsCache>>({});
  const [eyeRow, setEyeRow] = useState<{ row: PipeStatusInventoryRow; project: Project } | null>(
    null
  );
  const [isPrefetching, setIsPrefetching] = useState(true);
  const prefetchGenRef = useRef(0);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedPipeSizes, setSelectedPipeSizes] = useState<string[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [searchBusy, setSearchBusy] = useState(false);
  /** null = browse all; object (maybe empty) = search finished and tree is filtered to hits only. */
  const [searchHits, setSearchHits] = useState<SearchHits | null>(null);
  const [displayUnit, setDisplayUnit] = useState<'in' | 'mm'>('in');
  const searchGenRef = useRef(0);

  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<Set<string>>(() => new Set());
  const selectedGroupIdRef = useRef<string | null>(null);
  const collapsedGroupIdsRef = useRef<Set<string>>(new Set());
  selectedGroupIdRef.current = selectedGroupId;
  collapsedGroupIdsRef.current = collapsedGroupIds;

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchQuery.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [searchQuery]);

  const fitterProjects = useMemo(
    () =>
      projectList
        .filter((p) => isProjectPipeFitterEnabled(p))
        .sort((a, b) =>
          formatProjectLabel(a).localeCompare(formatProjectLabel(b), undefined, {
            sensitivity: 'base',
          })
        ),
    [projectList]
  );

  const fitterProjectKey = useMemo(
    () => fitterProjects.map((p) => String(p.projectId)).join('|'),
    [fitterProjects]
  );

  // Load ALL fitter project setups in parallel; keep spinner until every project finishes.
  useEffect(() => {
    if (fitterProjects.length === 0) {
      setSetupByProject({});
      setIsPrefetching(false);
      return;
    }

    const gen = ++prefetchGenRef.current;
    setIsPrefetching(true);

    void (async () => {
      const results = await Promise.all(
        fitterProjects.map(async (project) => {
          const id = String(project.projectId);
          try {
            const details = await fetchAllPipeDetails(project.projectId);
            const extracted = extractPipeSizesFromPipeDetails(details, project);
            const pipes = buildFitterSetupTree(extracted);
            return {
              id,
              cache: { loading: false, error: null, pipes } as SetupCache,
            };
          } catch (err: unknown) {
            return {
              id,
              cache: {
                loading: false,
                error: err instanceof Error ? err.message : 'Failed to load setup',
                pipes: [],
              } as SetupCache,
            };
          }
        })
      );

      if (prefetchGenRef.current !== gen) return;

      const next: Record<string, SetupCache> = {};
      for (const { id, cache } of results) {
        next[setupKey(id)] = cache;
      }
      setSetupByProject(next);
      setIsPrefetching(false);
    })();
    // Only re-fetch when the set of project ids changes (not on every fitterProjects array identity).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitterProjectKey]);

  const projectsWithGroups = useMemo(
    () =>
      fitterProjects.filter(
        (p) => (setupByProject[setupKey(String(p.projectId))]?.pipes?.length ?? 0) > 0
      ),
    [fitterProjects, setupByProject]
  );

  const filterPipes = useCallback(
    (pipes: FitterSetupPipeSize[]): FitterSetupPipeSize[] =>
      pipes
        .filter((pipe) =>
          selectedPipeSizes.length === 0 ? true : selectedPipeSizes.includes(pipe.pipeId)
        )
        .map((pipe) => ({
          ...pipe,
          groupIds: pipe.groupIds
            .filter(isRealGroupId)
            .filter((g) =>
              selectedGroupIds.length === 0 ? true : selectedGroupIds.includes(g)
            ),
        }))
        .filter((pipe) => pipe.groupIds.length > 0),
    [selectedPipeSizes, selectedGroupIds]
  );

  const basePipesByProjectId = useMemo(() => {
    const map: Record<string, FitterSetupPipeSize[]> = {};
    for (const p of projectsWithGroups) {
      const id = String(p.projectId);
      const pipes = filterPipes(setupByProject[setupKey(id)]?.pipes ?? []);
      if (pipes.length > 0) map[id] = pipes;
    }
    return map;
  }, [projectsWithGroups, setupByProject, filterPipes]);

  // When Pipe Id search is active, keep only projects / sizes / groups that actually matched.
  const pipesByProjectId = useMemo(() => {
    if (searchHits == null) return basePipesByProjectId;

    const map: Record<string, FitterSetupPipeSize[]> = {};
    Array.from(Object.entries(searchHits)).forEach(([, hit]) => {
      const basePipes = basePipesByProjectId[hit.projectId] ?? [];
      const basePipe = basePipes.find((p) => p.pipeId === hit.pipeId);
      if (!basePipe) return;
      const existing = map[hit.projectId] ?? [];
      const found = existing.find((p) => p.pipeId === hit.pipeId);
      if (found) {
        if (!found.groupIds.includes(hit.groupId)) {
          found.groupIds = [...found.groupIds, hit.groupId];
        }
      } else {
        map[hit.projectId] = [...existing, { ...basePipe, groupIds: [hit.groupId] }];
      }
    });
    return map;
  }, [basePipesByProjectId, searchHits]);

  const projects = useMemo(
    () => projectsWithGroups.filter((p) => (pipesByProjectId[String(p.projectId)]?.length ?? 0) > 0),
    [projectsWithGroups, pipesByProjectId]
  );

  const groupCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    if (searchHits != null) {
      Array.from(Object.entries(searchHits)).forEach(([navId, hit]) => {
        counts[navId] = hit.rows.length;
      });
      return counts;
    }
    for (const p of projects) {
      const projectId = String(p.projectId);
      for (const pipe of pipesByProjectId[projectId] ?? []) {
        for (const groupId of pipe.groupIds) {
          const navId = fitterGroupNodeId(projectId, pipe.pipeId, groupId);
          const key = rowsKey(projectId, pipe.pipeId, groupId);
          const cache = rowsByLeaf[key];
          counts[navId] = cache?.totalCount ?? cache?.rows?.length ?? 0;
        }
      }
    }
    return counts;
  }, [projects, pipesByProjectId, rowsByLeaf, searchHits]);

  const { tree: groupTree, leafRefs } = useMemo(
    () => buildFitterNavTree(projects, pipesByProjectId, formatProjectLabel, groupCounts),
    [projects, pipesByProjectId, groupCounts]
  );

  const groupConfigKey = useMemo(
    () =>
      groupTree
        .map((n) => (n.type === 'group' ? n.id : ''))
        .join('\0'),
    [groupTree]
  );

  useEffect(() => {
    const resolved = resolvePipeGroupSelection(
      groupTree,
      selectedGroupIdRef.current,
      collapsedGroupIdsRef.current,
      { resetSelection: false }
    );
    setSelectedGroupId((prev) =>
      prev === resolved.selectedGroupId ? prev : resolved.selectedGroupId
    );
    setCollapsedGroupIds((prev) => {
      if (
        prev.size === resolved.collapsedGroupIds.size &&
        Array.from(prev).every((id) => resolved.collapsedGroupIds.has(id))
      ) {
        return prev;
      }
      return resolved.collapsedGroupIds;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-resolve when tree shape changes
  }, [groupConfigKey]);

  const leafGroupIdsForSelection = useMemo(
    () => collectFitterLeafGroupIds(groupTree, selectedGroupId),
    [groupTree, selectedGroupId]
  );

  const pipeSizeOptions: Option[] = useMemo(() => {
    const byId = new Map<string, string>();
    for (const project of projectsWithGroups) {
      for (const pipe of setupByProject[setupKey(String(project.projectId))]?.pipes ?? []) {
        if (!byId.has(pipe.pipeId)) byId.set(pipe.pipeId, pipe.label);
      }
    }
    return Array.from(byId.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [projectsWithGroups, setupByProject]);

  const groupIdOptions: Option[] = useMemo(() => {
    const ids = new Set<string>();
    for (const project of projectsWithGroups) {
      for (const pipe of setupByProject[setupKey(String(project.projectId))]?.pipes ?? []) {
        if (selectedPipeSizes.length > 0 && !selectedPipeSizes.includes(pipe.pipeId)) continue;
        for (const g of pipe.groupIds) {
          if (isRealGroupId(g)) ids.add(g);
        }
      }
    }
    return Array.from(ids)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      .map((value) => ({ value, label: value }));
  }, [projectsWithGroups, setupByProject, selectedPipeSizes]);

  const activeFilterCount = selectedPipeSizes.length + selectedGroupIds.length;
  const hasActiveFilters = activeFilterCount > 0 || searchQuery.trim() !== '';

  const onResetFilters = () => {
    setSearchQuery('');
    setDebouncedSearch('');
    setSelectedPipeSizes([]);
    setSelectedGroupIds([]);
    setSearchHits(null);
    setSearchBusy(false);
  };

  const loadRows = useCallback(
    async (
      project: Project,
      pipeId: string,
      groupId: string,
      page = 1,
      search?: string
    ) => {
      const key = rowsKey(String(project.projectId), pipeId, groupId);
      const searchTerm = String(search ?? '').trim();
      setRowsByLeaf((prev) => ({
        ...prev,
        [key]: { ...prev[key], loading: true, error: null, page, search: searchTerm },
      }));
      try {
        const result = await fetchPipeStatusInventory({
          projectId: project.projectId,
          projectNumber: project.projectNumber,
          projectName: project.projectName,
          pipe_id: pipeId,
          groupId,
          page,
          pageSize: ROW_PAGE_SIZE,
          search: searchTerm || null,
        });
        setRowsByLeaf((prev) => ({
          ...prev,
          [key]: {
            loading: false,
            error: null,
            rows:
              page > 1 ? [...(prev[key]?.rows ?? []), ...result.rows] : result.rows,
            totalCount: result.totalCount,
            page,
            search: searchTerm,
          },
        }));
      } catch (err: unknown) {
        setRowsByLeaf((prev) => ({
          ...prev,
          [key]: {
            loading: false,
            error: err instanceof Error ? err.message : 'Failed to load pipe status rows',
            rows: [],
            totalCount: 0,
            page,
            search: searchTerm,
          },
        }));
      }
    },
    []
  );

  // Browse mode: load rows for selected nav leaf groups. Skip while Pipe Id search owns the data.
  useEffect(() => {
    if (isPrefetching || searchHits != null || leafGroupIdsForSelection.length === 0) return;
    for (const navId of leafGroupIdsForSelection) {
      const ref = leafRefs[navId];
      if (!ref) continue;
      const key = rowsKey(String(ref.project.projectId), ref.pipe.pipeId, ref.groupId);
      const existing = rowsByLeaf[key];
      const needsFetch = !existing?.rows || Boolean(existing.search);
      if (needsFetch && !existing?.loading) {
        void loadRows(ref.project, ref.pipe.pipeId, ref.groupId, 1);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leafGroupIdsForSelection, leafRefs, isPrefetching, loadRows, searchHits]);

  // Pipe Id search: keep only matching project → size → group; hide everything else.
  useEffect(() => {
    const q = debouncedSearch;
    if (!q) {
      setSearchHits(null);
      setSearchBusy(false);
      return;
    }
    if (isPrefetching) return;

    const gen = ++searchGenRef.current;
    setSearchBusy(true);
    setSearchHits(null);
    const qLower = q.toLowerCase();

    void (async () => {
      const hits: SearchHits = {};
      const rowUpdates: Record<string, RowsCache> = {};

      await Promise.all(
        projectsWithGroups.map(async (project) => {
          const projectId = String(project.projectId);
          const pipes = basePipesByProjectId[projectId] ?? [];
          if (pipes.length === 0) return;

          try {
            const result = await fetchPipeStatusInventory({
              projectId: project.projectId,
              projectNumber: project.projectNumber,
              projectName: project.projectName,
              search: q,
              page: 1,
              pageSize: 100,
            });

            const allowed = new Map(
              pipes.map((p) => [p.pipeId, new Set(p.groupIds.filter(isRealGroupId))])
            );

            for (const row of result.rows) {
              if (!row.pipeID.toLowerCase().includes(qLower)) continue;
              const pipeId = String(row.pipe_id ?? '').trim();
              const groupId = String(row.groupId ?? '').trim();
              const groups = allowed.get(pipeId);
              if (!groups || !isRealGroupId(groupId) || !groups.has(groupId)) continue;

              const navId = fitterGroupNodeId(projectId, pipeId, groupId);
              const existingHit = hits[navId];
              if (existingHit) {
                if (!existingHit.rows.some((r) => r.pipeID === row.pipeID)) {
                  existingHit.rows.push(row);
                }
              } else {
                hits[navId] = { projectId, pipeId, groupId, rows: [row] };
              }
            }
          } catch {
            // ignore per-project failures
          }
        })
      );

      if (searchGenRef.current !== gen) return;

      Array.from(Object.entries(hits)).forEach(([, hit]) => {
        const key = rowsKey(hit.projectId, hit.pipeId, hit.groupId);
        rowUpdates[key] = {
          loading: false,
          error: null,
          rows: hit.rows,
          totalCount: hit.rows.length,
          page: 1,
          search: q,
        };
      });

      setRowsByLeaf((prev) => ({ ...prev, ...rowUpdates }));
      setSearchHits(hits);

      const matchNavIds = Object.keys(hits);
      const firstMatchNavId = matchNavIds[0] ?? null;
      setSelectedGroupId(firstMatchNavId);

      if (firstMatchNavId) {
        setCollapsedGroupIds((prev) => {
          const next = new Set(prev);
          for (const navId of matchNavIds) {
            const p = parseFitterGroupNodeId(navId);
            if (!p) continue;
            next.delete(`fitter:project:${p.projectId}`);
            next.delete(`fitter:pipe:${p.projectId}|${p.pipeId}`);
          }
          return next;
        });
      }

      setSearchBusy(false);
    })();
  }, [debouncedSearch, isPrefetching, projectsWithGroups, basePipesByProjectId]);

  const toggleNavGroup = (id: string) => {
    setCollapsedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAllGroups = () => setCollapsedGroupIds(new Set());
  const collapseAllGroups = () =>
    setCollapsedGroupIds(collectCollapsibleGroupIds(groupTree));

  const formatNavLabel = (_field: PipeGroupField, label: string) => label;

  type TableRowModel = {
    navId: string;
    row: PipeStatusInventoryRow;
    project: Project;
    pipe: FitterSetupPipeSize;
    groupId: string;
  };

  const tableRows: TableRowModel[] = useMemo(() => {
    const out: TableRowModel[] = [];
    const qLower = debouncedSearch.toLowerCase();
    for (const navId of leafGroupIdsForSelection) {
      const ref = leafRefs[navId];
      if (!ref) continue;
      const key = rowsKey(String(ref.project.projectId), ref.pipe.pipeId, ref.groupId);
      const rows = rowsByLeaf[key]?.rows ?? [];
      for (const row of rows) {
        if (qLower && !row.pipeID.toLowerCase().includes(qLower)) continue;
        out.push({
          navId,
          row,
          project: ref.project,
          pipe: ref.pipe,
          groupId: ref.groupId,
        });
      }
    }
    return out;
  }, [leafGroupIdsForSelection, leafRefs, rowsByLeaf, debouncedSearch]);

  const selectionLoading = leafGroupIdsForSelection.some((navId) => {
    const ref = leafRefs[navId];
    if (!ref) return false;
    const key = rowsKey(String(ref.project.projectId), ref.pipe.pipeId, ref.groupId);
    return Boolean(rowsByLeaf[key]?.loading);
  });

  const selectionError = leafGroupIdsForSelection
    .map((navId) => {
      const ref = leafRefs[navId];
      if (!ref) return null;
      const key = rowsKey(String(ref.project.projectId), ref.pipe.pipeId, ref.groupId);
      return rowsByLeaf[key]?.error ?? null;
    })
    .find(Boolean);

  const unitSuffix = unitHeaderSuffix(displayUnit);

  return (
    <div className="flex min-h-0 flex-col gap-3">
      <CollapsibleFilterBar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search by Pipe Id…"
        activeFilterCount={activeFilterCount}
        filteredCount={tableRows.length}
        filteredCountLabel="pipes"
        onResetFilters={onResetFilters}
      >
        <MultiSelect
          options={pipeSizeOptions}
          selected={selectedPipeSizes}
          onChange={setSelectedPipeSizes}
          placeholder="Pipe Size"
        />
        <MultiSelect
          options={groupIdOptions}
          selected={selectedGroupIds}
          onChange={setSelectedGroupIds}
          placeholder="Group Id"
        />
      </CollapsibleFilterBar>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[12px] text-heritage-grey">
          Grouped by Project → Pipe size → Group Id
        </p>
        <div className="flex shrink-0 items-center gap-2 rounded-lg border border-[#E2E5EA] bg-white px-3 py-1.5">
          <Label
            htmlFor="fitter-inventory-unit"
            className={cn(
              'cursor-pointer text-xs font-semibold',
              displayUnit === 'in' ? 'text-blueprint-blue' : 'text-heritage-grey'
            )}
          >
            Inch
          </Label>
          <Switch
            id="fitter-inventory-unit"
            checked={displayUnit === 'mm'}
            onCheckedChange={(checked: boolean) => setDisplayUnit(checked ? 'mm' : 'in')}
            aria-label="Toggle display unit between Inch and MM"
          />
          <Label
            htmlFor="fitter-inventory-unit"
            className={cn(
              'cursor-pointer text-xs font-semibold',
              displayUnit === 'mm' ? 'text-blueprint-blue' : 'text-heritage-grey'
            )}
          >
            MM
          </Label>
        </div>
      </div>

      {searchBusy && (
        <p className="inline-flex items-center gap-2 text-[12px] text-heritage-grey">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Searching Pipe Id…
        </p>
      )}

      <div className="flex h-[calc(100vh-320px)] min-h-[280px] overflow-hidden rounded-lg border border-[#E2E5EA] bg-white">
        {isPrefetching || (Boolean(debouncedSearch) && searchHits == null) ? (
          <div className="flex flex-1 items-center justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#e04530]" />
          </div>
        ) : groupTree.length === 0 ? (
          <div className="flex flex-1 items-center justify-center py-8 text-sm text-heritage-grey">
            {debouncedSearch
              ? 'No Pipe Id matches the search.'
              : hasActiveFilters
                ? 'No projects match the current filters.'
                : 'No projects with Pipe Fitter sizes that have a group id.'}
          </div>
        ) : (
          <>
            <PipeGroupNavPanel
              tree={groupTree}
              collapsedIds={collapsedGroupIds}
              selectedGroupId={selectedGroupId}
              onToggle={toggleNavGroup}
              onSelect={setSelectedGroupId}
              onExpandAll={expandAllGroups}
              onCollapseAll={collapseAllGroups}
              formatLabel={formatNavLabel}
            />
            <div className="min-h-0 min-w-0 flex-1 overflow-auto">
              <Table
                className="w-max min-w-full border-separate border-spacing-0"
                containerClassName="relative w-full"
              >
                <TableHeader className="sticky top-0 z-20 border-b border-[#E2E5EA] bg-white">
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Pipe Id</TableHead>
                    <TableHead className="text-center">
                      Outer diameter [{unitSuffix}]
                    </TableHead>
                    <TableHead className="text-center">
                      Wall thickness [{unitSuffix}]
                    </TableHead>
                    <TableHead className="text-center">Group</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="sticky right-0 z-20 w-[80px] bg-white text-center">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className="[&_tr>td]:border-b [&_tr>td]:border-[#E2E5EA]">
                  {selectionLoading && tableRows.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="min-h-[220px] border-b border-[#E2E5EA] py-16 text-center align-middle"
                      >
                        <div className="flex items-center justify-center">
                          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#e04530]" />
                        </div>
                      </TableCell>
                    </TableRow>
                  )}

                  {!selectionLoading && selectionError && (
                    <TableRow>
                      <TableCell colSpan={6} className="py-8 text-center text-readable-red">
                        {selectionError}
                      </TableCell>
                    </TableRow>
                  )}

                  {!selectionLoading && !selectionError && tableRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="py-8 text-center text-heritage-grey">
                        No pipes in this group
                      </TableCell>
                    </TableRow>
                  )}

                  {tableRows.map(({ row, project, pipe, groupId }) => {
                    const materialExists = inventoryHasMaterialForPipeId(materials, {
                      projectId: project.projectId,
                      pipeID: row.pipeID,
                    });
                    const showEye = hasPipeData(row);
                    return (
                      <TableRow key={`${project.projectId}|${pipe.pipeId}|${groupId}|${row.pipeID}`}>
                        <TableCell className="font-medium text-text-grey">{row.pipeID}</TableCell>
                        <TableCell className="text-center">
                          {formatStoredInches(row.pipeSize ?? pipe.size, displayUnit)}
                        </TableCell>
                        <TableCell className="text-center">
                          {formatStoredInches(
                            row.wallThickness ?? pipe.wallThickness,
                            displayUnit
                          )}
                        </TableCell>
                        <TableCell className="text-center">{groupId}</TableCell>
                        <TableCell className="text-center">
                          <span
                            className={cn(
                              'inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold',
                              statusBadgeClass(row.status)
                            )}
                          >
                            {row.status || '—'}
                          </span>
                        </TableCell>
                        <TableCell className="sticky right-0 z-10 bg-white text-center">
                          <div className="flex items-center justify-center gap-2">
                            {showEye && (
                              <Button
                                type="button"
                                variant="outline"
                                className="flex h-8 w-8 items-center justify-center p-0 text-blue-600 hover:text-blue-800"
                                title="View Pipe Data"
                                aria-label="View Pipe Data"
                                onClick={() => setEyeRow({ row, project })}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            )}
                            {materialExists ? (
                              <Tooltip
                                content="Material already exists"
                                contentClassName="min-w-0"
                              >
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="flex h-8 w-8 items-center justify-center p-0 text-blue-600 hover:text-blue-800"
                                  disabled
                                  aria-label="Material already exists"
                                >
                                  <Plus className="h-4 w-4" />
                                </Button>
                              </Tooltip>
                            ) : onAddFromFitter ? (
                              <Button
                                type="button"
                                variant="outline"
                                className="flex h-8 w-8 items-center justify-center p-0 text-blue-600 hover:text-blue-800"
                                title="Add material"
                                aria-label="Add material"
                                onClick={() =>
                                  onAddFromFitter({
                                    projectId: String(project.projectId),
                                    projectNumber: project.projectNumber,
                                    projectName: project.projectName,
                                    pipeNo: row.pipeID,
                                    pipePipeId: String(row.pipe_id || pipe.pipeId),
                                  })
                                }
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>

      {eyeRow && (
        <PipeFfitter
          isOpen
          isPipeFitterOpen={false}
          onClose={() => setEyeRow(null)}
          selectedMaterial={{
            details: {
              pipeNo: eyeRow.row.pipeID,
              pipePipeId: eyeRow.row.pipe_id,
            },
          }}
          selectedPipe={null}
          project={
            {
              project: eyeRow.project,
              team: [],
              documents: [],
              materials: [],
            } as DashboardProject
          }
          unit={eyeRow.project.unit || 'in'}
          pipeStatusRow={{
            pipeID: eyeRow.row.pipeID,
            pipeData: eyeRow.row.pipeData as never,
          }}
        />
      )}
    </div>
  );
};
